import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Car, Lock, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ResetPasswordPage = () => {
  const { i18n } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(i18n.language === 'es' 
        ? 'Las contraseñas no coinciden' 
        : 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error(i18n.language === 'es' 
        ? 'La contraseña debe tener al menos 6 caracteres' 
        : 'Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      if (response.ok) {
        setSuccess(true);
        toast.success(i18n.language === 'es' 
          ? 'Contraseña actualizada correctamente' 
          : 'Password updated successfully');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        const data = await response.json();
        setError(true);
        toast.error(data.detail || 'Error al restablecer la contraseña');
      }
    } catch (err) {
      setError(true);
      toast.error(i18n.language === 'es' 
        ? 'Error de conexión' 
        : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
            <Car className="w-7 h-7 text-black" />
          </div>
          <span className="font-heading font-bold text-2xl text-white">PLEYADES</span>
        </Link>

        {/* Card */}
        <div className="card-dark p-8">
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white">
                {i18n.language === 'es' ? 'Contraseña actualizada' : 'Password updated'}
              </h1>
              <p className="text-zinc-400 text-sm">
                {i18n.language === 'es' 
                  ? 'Tu contraseña ha sido actualizada correctamente. Redirigiendo al login...'
                  : 'Your password has been updated successfully. Redirecting to login...'}
              </p>
            </div>
          ) : error ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white">
                {i18n.language === 'es' ? 'Enlace inválido' : 'Invalid link'}
              </h1>
              <p className="text-zinc-400 text-sm">
                {i18n.language === 'es' 
                  ? 'El enlace ha expirado o no es válido. Solicita uno nuevo.'
                  : 'The link has expired or is invalid. Request a new one.'}
              </p>
              <Link to="/forgot-password">
                <Button className="btn-primary mt-4">
                  {i18n.language === 'es' ? 'Solicitar nuevo enlace' : 'Request new link'}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
                {i18n.language === 'es' ? 'Nueva contraseña' : 'New password'}
              </h1>
              <p className="text-zinc-400 text-sm text-center mb-6">
                {i18n.language === 'es' 
                  ? 'Ingresa tu nueva contraseña.'
                  : 'Enter your new password.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-white">
                    {i18n.language === 'es' ? 'Nueva contraseña' : 'New password'}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="input-gold pl-10"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white">
                    {i18n.language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="input-gold pl-10"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary"
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {i18n.language === 'es' ? 'Guardando...' : 'Saving...'}
                    </>
                  ) : (
                    i18n.language === 'es' ? 'Guardar contraseña' : 'Save password'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
